import { isNil } from "lopermut"
import { z } from "zod"

export function requiredString(error: string) {
  return z.string({ error }).transform((data) => {
    if (!data || data.length === 0) throw new Error(error)
    return data
  })
}

export function optionalString(error: string) {
  return z
    .string({ error })
    .optional()
    .nullable()
    .transform((data) => (!data || data.length === 0 ? undefined : data))
}

export function requiredStringOrNumberToNumber(error: string) {
  return z.union([z.string(), z.number()], { error }).transform((val) => {
    if (typeof val === "number") return val
    if (typeof val === "string") {
      const normalized = val.replace(/,/g, ".").replace(/\.(?=.*\.)/g, "")
      const parsed = parseFloat(normalized)
      if (!isNaN(parsed)) return Number(parsed)
    }
    throw new Error(error)
  })
}

export function optionalStringOrNumberToNumber({
  invalid_type_error,
}: {
  invalid_type_error?: string
} = {}) {
  return z
    .union([z.string(), z.number(), z.undefined(), z.null()], {
      error: (issue) => (issue.input === undefined ? invalid_type_error : invalid_type_error),
    })
    .transform((val) => {
      if (typeof val === "number") return val
      if (typeof val === "string") {
        if (val.length === 0) return undefined

        const normalized = val.replace(/,/g, ".").replace(/\.(?=.*\.)/g, "")
        const parsed = parseFloat(normalized)
        if (!isNaN(parsed)) return Number(parsed)
        else return val
      }
      if (isNil(val)) return val
      return undefined
    })
    .refine((val) => isNil(val) || typeof val === "number", {
      error: invalid_type_error,
    })
    .optional()
}

export function requiredStringOrDateToDate(error: string) {
  return z
    .union([z.string(), z.date()], { error })
    .transform((val) => {
      if (val instanceof Date) return val
      if (typeof val === "string") {
        if (val.length === 0) return undefined

        const parsed = new Date(val)
        if (!isNaN(parsed.getTime())) return parsed
      }
      throw new Error(error)
    })
    .refine((val) => val instanceof Date || typeof val === "string", {
      message: error,
    })
}

export function optionalStringOrDateToDate(error: string) {
  return z
    .union([z.string(), z.date()], { error })
    .transform((val) => {
      if (val instanceof Date) return val
      if (typeof val === "string") {
        const parsed = new Date(val)
        if (!isNaN(parsed.getTime())) return parsed
      }
    })
    .optional()
    .nullable()
}

export function optionalStringOrBooleanToBoolean(error?: string) {
  return z
    .union(
      [
        z.literal(""),
        z.union([z.literal("true"), z.literal("false")]),
        z.boolean(),
        z.undefined(),
        z.null(),
      ],
      { error },
    )
    .transform((val) => {
      if (isNil(val) || val === "") return undefined
      if (typeof val === "boolean") return val
      if (val === "true" || val === "false") return val === "true"
      throw new Error(error)
    })
}

export const PrismaJsonSchema = z
  .union([z.record(z.string(), z.any()), z.string(), z.number(), z.boolean()])
  .transform((data) => {
    if (Array.isArray(data)) return data
    if (typeof data === "object") return data

    return null
  })
